/**
 * Dashboard Notification Store (Zustand)
 *
 * SEPARATE notification system for dashboard-specific alerts.
 * Completely independent from the main AppShell notification store.
 *
 * Dashboard-Specific Notifications:
 * 1. Scheduled PUG First-Time Filer Lead Alerts (9:00 AM, 12:00 PM, 4:30 PM)
 * 2. HMC Tracker Submissions (copied from main store)
 *
 * This store is NOT persisted to localStorage - dashboard notifications are ephemeral.
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

/**
 * Dashboard-specific notification types
 */
export type DashboardNotificationType =
  | 'dashboard_pug_leads'       // PUG first-time filer lead alerts grouped by market
  | 'hmc_new_submission';       // HMC ticket request submissions

/**
 * Dashboard notification severity levels
 */
export type DashboardNotificationSeverity = 'info' | 'success' | 'warning';

/**
 * Dashboard notification object
 */
export interface DashboardNotification {
  /** Unique notification ID */
  id: string;

  /** Type of notification */
  type: DashboardNotificationType;

  /** Display title */
  title: string;

  /** Notification message/description */
  message: string;

  /** Severity level (determines color/icon) */
  severity: DashboardNotificationSeverity;

  /** Timestamp when notification was created */
  timestamp: string; // ISO 8601 format

  /** Whether notification has been read */
  read: boolean;

  /** Optional action URL (for "More Info" or related page) */
  actionUrl?: string;

  /** Optional metadata for context */
  metadata?: {
    market?: string;      // For PUG leads: 'houston' | 'austin'
    count?: number;       // For PUG leads: number of leads
    [key: string]: any;
  };
}

/**
 * Dashboard notification store state and actions
 */
interface DashboardNotificationStore {
  // State
  notifications: DashboardNotification[];

  // Actions
  addNotification: (notification: Omit<DashboardNotification, 'id' | 'timestamp' | 'read'>) => string;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (notificationId: string) => void;
  clearAllNotifications: () => void;
}

/**
 * Generate unique notification ID
 */
function generateNotificationId(): string {
  return `dashboard_notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create dashboard notification store (NO PERSISTENCE)
 */
export const useDashboardNotificationStore = create<DashboardNotificationStore>((set) => ({
  // Initial state
  notifications: [],

  /**
   * Add a new notification
   *
   * @param notification - Notification data (without id, timestamp, read)
   * @returns Generated notification ID
   */
  addNotification: (notification) => {
    const id = generateNotificationId();
    const timestamp = new Date().toISOString();

    const newNotification: DashboardNotification = {
      ...notification,
      id,
      timestamp,
      read: false,
    };

    set((state) => ({
      notifications: [newNotification, ...state.notifications],
    }));

    console.log(`[DashboardNotificationStore] Added notification:`, newNotification);
    return id;
  },

  /**
   * Mark notification as read
   *
   * @param notificationId - ID of notification to mark as read
   */
  markAsRead: (notificationId) => {
    set((state) => ({
      notifications: state.notifications.map((n) =>
        n.id === notificationId ? { ...n, read: true } : n
      ),
    }));
    console.log(`[DashboardNotificationStore] Marked as read: ${notificationId}`);
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: () => {
    set((state) => ({
      notifications: state.notifications.map((n) => ({ ...n, read: true })),
    }));
    console.log(`[DashboardNotificationStore] Marked all as read`);
  },

  /**
   * Delete a notification
   *
   * @param notificationId - ID of notification to delete
   */
  deleteNotification: (notificationId) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== notificationId),
    }));
    console.log(`[DashboardNotificationStore] Deleted notification: ${notificationId}`);
  },

  /**
   * Clear all notifications
   */
  clearAllNotifications: () => {
    set({ notifications: [] });
    console.log(`[DashboardNotificationStore] Cleared all notifications`);
  },
}));

/**
 * Helper hook to get unread count
 */
export function useDashboardUnreadCount() {
  return useDashboardNotificationStore((state) =>
    state.notifications.filter((n) => !n.read).length
  );
}

/**
 * Helper hook to get unread notifications
 */
export function useDashboardUnreadNotifications() {
  return useDashboardNotificationStore(
    useShallow((state) => state.notifications.filter((n) => !n.read))
  );
}

/**
 * Helper hook to get recent notifications (last N)
 */
export function useDashboardRecentNotifications(limit = 6) {
  return useDashboardNotificationStore(
    useShallow((state) => state.notifications.slice(0, limit))
  );
}

/**
 * Helper hook to get notification actions only
 */
export function useDashboardNotificationActions() {
  return useDashboardNotificationStore(
    useShallow((state) => ({
      addNotification: state.addNotification,
      markAsRead: state.markAsRead,
      markAllAsRead: state.markAllAsRead,
      deleteNotification: state.deleteNotification,
      clearAllNotifications: state.clearAllNotifications,
    }))
  );
}
